import { z } from 'zod';
import { timestampSchema } from './admin.js';
import { featureFlagNames, featureFlagStateSchema } from './feature-flags.js';

/** System overview for administrators (ADM-06): GET /api/v1/admin/system. */

/** Sessions refreshed within this window count as online (refresh at most every 5 minutes). */
export const ONLINE_WINDOW_MINUTES = 15;
/** Warning when the AI costs of the month reach this share of the budget. */
export const AI_BUDGET_WARNING_SHARE = 0.8;
/** Warning when a nightly job has not completed for this long. */
export const NIGHTLY_JOB_MAX_AGE_HOURS = 26;
/** Warning when the last successful backup is older (nightly backup plus slack). */
export const BACKUP_MAX_AGE_HOURS = 36;
/**
 * Failed and blocked sign-ins within one hour from which the overview warns and administrators
 * get an e-mail (at most every SECURITY_ALERT_PAUSE_HOURS).
 */
export const SECURITY_ALERT_FAILURES_PER_HOUR = 20;
export const SECURITY_ALERT_PAUSE_HOURS = 6;
/** Warning this many days before the Entra client secret expires. */
export const ENTRA_SECRET_WARNING_DAYS = 30;

const countPair = z.object({ day: z.number().int(), week: z.number().int() });

export const systemHintCodes = [
  'job_failed',
  'job_overdue',
  'mail_failed',
  'migrations_pending',
  'ai_budget',
  'users_without_access',
  'break_glass_sign_in',
  'sign_ins_blocked',
  'sign_in_attack',
  'backup_failed',
  'backup_overdue',
  'backup_missing',
  'entra_secret_expiring',
  'entra_secret_expired',
  'entra_secret_unknown',
] as const;
export type SystemHintCode = (typeof systemHintCodes)[number];

export const systemHintSchema = z.object({
  code: z.enum(systemHintCodes),
  /** Only warnings count in the badge of the admin menu. */
  severity: z.enum(['warning', 'info']),
  /** Job name, number of people, share of the budget, … */
  subject: z.string().nullable(),
  count: z.number().nullable(),
});
export type SystemHint = z.infer<typeof systemHintSchema>;

export const systemJobSchema = z.object({
  name: z.string(),
  lastCompletedAt: timestampSchema.nullable(),
  /** Result returned by the last completed run, e.g. `{ mails: 3, failed: 0 }`. */
  lastOutput: z.record(z.string(), z.unknown()).nullable(),
  failedWeek: z.number().int(),
  lastFailedAt: timestampSchema.nullable(),
  /** Runs once a night (cron at a fixed hour); overdue after NIGHTLY_JOB_MAX_AGE_HOURS. */
  nightly: z.boolean(),
});
export type SystemJob = z.infer<typeof systemJobSchema>;

export const backupRunSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  status: z.enum(['ok', 'failed']),
  startedAt: timestampSchema,
  finishedAt: timestampSchema,
  sizeBytes: z.number().int().nullable(),
  fileName: z.string().nullable(),
  error: z.string().nullable(),
});
export type BackupRun = z.infer<typeof backupRunSchema>;

export const systemOverviewSchema = z.object({
  generatedAt: timestampSchema,
  users: z.object({
    internal: z.object({ active: z.number().int(), deactivated: z.number().int() }),
    external: z.object({
      active: z.number().int(),
      invited: z.number().int(),
      deactivated: z.number().int(),
    }),
    invitations: z.object({ open: z.number().int(), expired: z.number().int() }),
    /** Internal, active, without global role and project membership (e.g. after JIT sign-in). */
    withoutAccess: z.object({
      count: z.number().int(),
      people: z.array(z.object({ id: z.uuid(), name: z.string(), createdAt: timestampSchema })),
    }),
    /** Active break-glass accounts; `recent`: the last signed in (at most 5). */
    breakGlass: z.object({
      count: z.number().int(),
      recent: z.array(
        z.object({ id: z.uuid(), name: z.string(), lastLoginAt: timestampSchema.nullable() }),
      ),
    }),
    online: z.number().int(),
    signIns: countPair,
  }),
  content: z.object({
    organizations: z.object({ active: z.number().int(), inactive: z.number().int() }),
    projects: z.object({ active: z.number().int(), archived: z.number().int() }),
    tasks: z.object({ open: z.number().int(), done: z.number().int(), trash: z.number().int() }),
    comments: z.number().int(),
    attachments: z.object({ count: z.number().int(), bytes: z.number().int() }),
  }),
  security: z.object({
    failedSignIns: countPair,
    /** Failed plus blocked sign-ins within the last hour (alerting). */
    failedLastHour: z.number().int(),
    blockedSignIns: countPair,
    rejectedMicrosoftSignIns: countPair,
    breakGlassSignIns: countPair,
  }),
  operations: z.object({
    version: z.string(),
    startedAt: timestampSchema,
    migrations: z.object({
      applied: z.number().int(),
      expected: z.number().int(),
      latest: z.string().nullable(),
    }),
    database: z.object({
      bytes: z.number().int(),
      largestTables: z.array(z.object({ name: z.string(), bytes: z.number().int() })),
    }),
    /** null while the job schema does not exist (jobs never started). */
    jobs: z.array(systemJobSchema).nullable(),
    mailFailuresDay: z.number().int().nullable(),
    /** Written by infra/backup/backup.sh; newest first. */
    backups: z.object({
      lastOk: backupRunSchema.nullable(),
      recent: z.array(backupRunSchema),
    }),
    ai: z
      .object({ costEur: z.number(), budgetEur: z.number(), requestsMonth: z.number().int() })
      .nullable(),
  }),
  config: z.object({
    flags: z.record(z.enum(featureFlagNames), featureFlagStateSchema),
    microsoftSignIn: z.boolean(),
    /** ENTRA_CLIENT_SECRET_EXPIRES, YYYY-MM-DD. */
    entraSecretExpires: z.iso.date().nullable(),
    attachmentStorage: z.boolean(),
    assistantProvider: z.string().nullable(),
    /** Host of the mail relay (no credentials). */
    smtpHost: z.string(),
  }),
  hints: z.array(systemHintSchema),
});
export type SystemOverview = z.infer<typeof systemOverviewSchema>;
